import { useAdminAccess } from "src/hooks/useAdminAccess";
import { useSudowriteStore } from "src/stores/sudowriteStore";
import useTrialDefaultAgenticChatModeExperiment from "../../hooks/useTrialDefaultAgenticChatModeExperiment";
import {
  ADMIN_AGENTIC_CHAT_BRAIN_MODELS,
  AGENTIC_CHAT_MODES,
  DEFAULT_ADMIN_AGENTIC_CHAT_BRAIN_MODEL,
  DEFAULT_AGENTIC_CHAT_MODE,
  isAdminAgenticChatBrainModel,
} from "~shared/constants";
import {
  chatComposerModeLabelVariant as agenticChatModeLabelVariant,
  getChatComposerModeLabels as getAgenticChatModeLabels,
} from "@sudowrite/shared-chat/labels";
import { DEFAULT_CHAT_TONE, DEFAULT_CHAT_RESPONSE_LENGTH } from "~shared/chatPersonalization";
import type { ChatTone, ChatResponseLength } from "~shared/chatPersonalization";

export const usePersistentChatUserSettings = () => {
  const userData = useSudowriteStore((state) => state.userData);
  const subscription = useSudowriteStore((state) => state.subscription);
  const { can } = useAdminAccess();
  const isAdmin = can("admin.tools");
  const adminBrainPickerEnabled =
    subscription?.hasAccessToAdminAgenticChatBrainPicker === true ||
    subscription?.features?.adminAgenticChatBrainPicker === true;
  const showAdminBrainPicker = isAdmin && adminBrainPickerEnabled;
  const { defaultMode: trialDefaultAgenticChatMode, shouldApplyDefault: shouldApplyTrialDefaultAgenticChatMode } =
    useTrialDefaultAgenticChatModeExperiment();
  const savedAgenticChatMode = userData?.data?.agenticChatMode?.value;
  const agenticChatMode =
    savedAgenticChatMode ??
    (shouldApplyTrialDefaultAgenticChatMode ? trialDefaultAgenticChatMode : DEFAULT_AGENTIC_CHAT_MODE.value);

  const storedBrainModel = userData?.data?.agenticChatBrainModel?.value;
  const agenticChatBrainModel =
    showAdminBrainPicker && isAdminAgenticChatBrainModel(storedBrainModel)
      ? storedBrainModel
      : DEFAULT_ADMIN_AGENTIC_CHAT_BRAIN_MODEL.value;

  const modeLabelVariant = agenticChatModeLabelVariant(subscription?.isFeedbackBetaUser === true);
  const composerModeLabels = getAgenticChatModeLabels(modeLabelVariant);

  const setAgenticChatMode = (modeValue: string) => {
    userData?.update({ agenticChatMode: { value: modeValue } });
  };

  const setAgenticChatBrainModel = (modelKey: string) => {
    if (!showAdminBrainPicker || !isAdminAgenticChatBrainModel(modelKey)) return;
    userData?.update({ agenticChatBrainModel: { value: modelKey } });
  };

  const getModeConfig = () => {
    const base = AGENTIC_CHAT_MODES.find((m) => m.value === agenticChatMode) ?? DEFAULT_AGENTIC_CHAT_MODE;
    if (modeLabelVariant === "default") {
      return base;
    }
    return {
      ...base,
      label: agenticChatMode === "write" ? composerModeLabels.writeLabel : composerModeLabels.chatLabel,
    };
  };

  const chatTone: ChatTone = userData?.data?.chatPersonalizationTone ?? DEFAULT_CHAT_TONE;
  const chatResponseLength: ChatResponseLength = userData?.data?.chatPersonalizationResponseLength ?? DEFAULT_CHAT_RESPONSE_LENGTH;
  const chatMemoryEnabled: boolean = userData?.data?.chatPersonalizationMemoryEnabled ?? true;
  const chatCustomInstructions: string = userData?.data?.chatPersonalizationCustomInstructions ?? "";

  const setChatTone = (tone: ChatTone) => {
    userData?.update({ chatPersonalizationTone: tone });
  };

  const setChatResponseLength = (length: ChatResponseLength) => {
    userData?.update({ chatPersonalizationResponseLength: length });
  };

  const setChatMemoryEnabled = (enabled: boolean) => {
    userData?.update({ chatPersonalizationMemoryEnabled: enabled });
  };

  const setChatCustomInstructions = (instructions: string) => {
    userData?.update({ chatPersonalizationCustomInstructions: instructions });
  };

  return {
    userSettings: {
      agenticChatMode,
      modeLabel: getModeConfig().label,
      agenticChatBrainModel,
      adminBrainModelOptions: ADMIN_AGENTIC_CHAT_BRAIN_MODELS,
      isAdmin: showAdminBrainPicker,
      getModelKey: () => (showAdminBrainPicker ? agenticChatBrainModel : getModeConfig().modelKey),
      getModeConfig,
      composerModeLabels,
      modeLabelVariant,
    },
    updateUserSettings: (p: { agenticChatMode?: string; agenticChatBrainModel?: string }) => {
      if (p.agenticChatMode !== undefined) {
        setAgenticChatMode(p.agenticChatMode);
      }
      if (p.agenticChatBrainModel !== undefined) {
        setAgenticChatBrainModel(p.agenticChatBrainModel);
      }
    },
    personalization: {
      chatTone,
      chatResponseLength,
      chatMemoryEnabled,
      chatCustomInstructions,
      setChatTone,
      setChatResponseLength,
      setChatMemoryEnabled,
      setChatCustomInstructions,
    },
  };
};
